'use client';

import { useEffect, useMemo, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';

import type { ReactionWithUser, UserProfile } from '../types';
import { useCurrentUser } from './use-current-user';

/**
 * Matches a canonical UUID. Optimistic chat messages carry a temporary
 * `temp-<timestamp>` id before the server returns the real UUID; those must
 * never reach a query/subscription filtering on a `uuid` column, or Postgres
 * throws `invalid input syntax for type uuid`.
 */
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Raw reaction data from database query
 */
interface ReactionQueryRow {
  id: string;
  message_id: string;
  user_id: string;
  emoji: string;
  created_at: string;
  user: UserProfile | null;
}

interface UseReactionsOptions {
  messageId: string | null;
  realtime?: boolean;
}

/**
 * Hook to get reactions for a message
 */
export function useReactions(options: UseReactionsOptions) {
  const { messageId, realtime = true } = options;
  const client = useSupabase();
  const queryClient = useQueryClient();
  const channelRef = useRef<ReturnType<typeof client.channel> | null>(null);

  // A `temp-...` optimistic id is truthy but not a UUID — treat it as absent so
  // neither the REST query nor the realtime filter sends a non-UUID value.
  const isPersisted = !!messageId && UUID_RE.test(messageId);

  const queryKey = useMemo(() => ['chat-reactions', messageId], [messageId]);

  const query = useQuery({
    queryKey,
    queryFn: async (): Promise<ReactionWithUser[]> => {
      if (!messageId) return [];

      const { data, error } = await client
        .from('message_reactions')
        .select(
          `
          *,
          user:profiles!message_reactions_user_id_fkey(id, first_name, last_name, profile_photo_url)
        `,
        )
        .eq('message_id', messageId)
        .order('created_at', { ascending: true });

      if (error) {
        throw error;
      }

      return ((data || []) as unknown as ReactionQueryRow[]).map(
        (reaction) => ({
          ...reaction,
          user: reaction.user ?? null,
        }),
      );
    },
    enabled: isPersisted,
    staleTime: 60000,
  });

  // Set up realtime subscription
  useEffect(() => {
    if (!realtime || !isPersisted) {
      return;
    }

    if (channelRef.current) {
      client.removeChannel(channelRef.current);
    }

    const channel = client
      .channel(`chat-reactions:${messageId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'message_reactions',
          filter: `message_id=eq.${messageId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey });
        },
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        client.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [client, messageId, isPersisted, realtime, queryClient, queryKey]);

  // Group reactions by emoji
  const groupedReactions = useMemo(() => {
    const groups = new Map<string, ReactionWithUser[]>();

    (query.data || []).forEach((reaction) => {
      const existing = groups.get(reaction.emoji) || [];
      groups.set(reaction.emoji, [...existing, reaction]);
    });

    return groups;
  }, [query.data]);

  // Get reaction counts
  const reactionCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    groupedReactions.forEach((reactions, emoji) => {
      counts[emoji] = reactions.length;
    });
    return counts;
  }, [groupedReactions]);

  return {
    ...query,
    reactions: query.data || [],
    groupedReactions,
    reactionCounts,
  };
}

/**
 * Hook for toggling reactions on messages
 * Uses the toggle_reaction RPC for atomic operation (single DB call)
 */
export function useToggleReaction() {
  const client = useSupabase();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      messageId,
      emoji,
    }: {
      messageId: string;
      emoji: string;
    }) => {
      // Use RPC for atomic toggle operation (1 DB call instead of 2)
      const { data, error } = await client.rpc('toggle_reaction', {
        p_message_id: messageId,
        p_emoji: emoji.trim(),
      });

      if (error) {
        throw new Error(error.message);
      }

      return { added: !!data };
    },

    onMutate: async ({ messageId }) => {
      await queryClient.cancelQueries({
        queryKey: ['chat-reactions', messageId],
      });

      const previousData = queryClient.getQueryData([
        'chat-reactions',
        messageId,
      ]);

      return { previousData, messageId };
    },

    onError: (_error, { messageId }, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          ['chat-reactions', messageId],
          context.previousData,
        );
      }
    },

    onSuccess: (_data, { messageId }) => {
      queryClient.invalidateQueries({
        queryKey: ['chat-reactions', messageId],
      });
      // Also invalidate messages to update reaction counts
      queryClient.invalidateQueries({
        queryKey: ['chat-messages'],
      });
    },
  });
}

/**
 * Hook to check if current user has reacted with a specific emoji
 */
export function useHasReacted(messageId: string | null, emoji: string) {
  const client = useSupabase();
  const { userId } = useCurrentUser();

  // Optimistic (`temp-...`) ids are truthy but not UUIDs — gate the query so it
  // never filters `message_id` (a uuid column) on a non-UUID value.
  const isPersisted = !!messageId && UUID_RE.test(messageId);

  return useQuery({
    queryKey: ['chat-has-reacted', messageId, emoji, userId],
    queryFn: async (): Promise<boolean> => {
      if (!isPersisted || !userId) return false;

      const { data, error } = await client
        .from('message_reactions')
        .select('id')
        .eq('message_id', messageId)
        .eq('user_id', userId)
        .eq('emoji', emoji.trim())
        .maybeSingle();

      if (error) {
        return false;
      }

      return !!data;
    },
    enabled: isPersisted && !!emoji && !!userId,
    staleTime: 30000,
  });
}
